import React, { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { t } from 'services/translation';

// https://github.com/catamphetamine/react-phone-number-input
import PhoneInput from 'react-phone-number-input/input';
import { Theme } from 'ui_system/theme';
import {
  countryCodes,
  getCountryName,
  getCountryCallingCode,
} from './countryUtils';

const inputHeight = '48px';
const countrySelectWidth = '80px';

const PhoneInputs = styled.div`
  display: flex;
  height: ${inputHeight};
`;

// Overriding global input styles
const StyledPhoneInput = styled(PhoneInput)`
  width: calc(100% - ${countrySelectWidth}) !important;
  height: 100% !important;
  border: 1px solid
    ${(props) =>
      props.hasError
        ? props.errorColor
        : Theme.colors.previewSubtleOutlineColor} !important;
  color: ${(props) =>
    props.hasError ? props.errorColor : 'initial'} !important;
  border-left: 0 !important;
  border-radius: 0 !important;
  padding: 10px 15px !important;

  &::placeholder {
    color: ${Theme.colors.previewGuideTextColor} !important;
  }

  &:focus {
    outline: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: ${countrySelectWidth};
`;

const Select = styled.select`
  appearance: none;
  position: absolute;
  opacity: 0;
  width: 100% !important;
  height: 100% !important;
  border: 1px solid ${Theme.colors.previewSubtleOutlineColor};
  &:hover {
    cursor: pointer;
  }
`;

const Button = styled.button`
  font-size: 16px;
  display: flex;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid
    ${(props) =>
      props.hasError
        ? props.errorColor
        : Theme.colors.borderActionableFieldColor};
  background-color: ${(props) =>
    props.hovered ? 'rgb(243, 243, 243)' : 'white'};
  padding: 10px;
`;

const ArrowDown = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid ${Theme.colors.previewIconColor};
  margin-right: 6px;
`;

export const PhoneNumberInput = ({
  phoneNumber,
  setPhoneNumber,
  countryCode: cc,
  hasError,
  errorColor,
  dataTestId,
  ariaLabelledBy,
}) => {
  const [hovered, setHovered] = useState(false);
  const [countryCode, setCountryCode] = useState(cc);

  function onCountryChange(value) {
    setCountryCode(value);
    // Swap calling codes if there is one
    const callingCode = `+${getCountryCallingCode(countryCode)}`;
    if (phoneNumber && phoneNumber.indexOf(callingCode) === 0) {
      const newCallingCode = `+${getCountryCallingCode(value)}`;
      setPhoneNumber(`${newCallingCode}${phoneNumber.split(callingCode)[1]}`);
    }
  }

  return (
    <PhoneInputs>
      <SelectContainer>
        <Button
          data-testid="phone-input-button"
          className="phone-input-button"
          hovered={hovered}
          hasError={hasError}
          errorColor={errorColor}
        >
          <ArrowDown />+{getCountryCallingCode(countryCode)}
        </Button>

        <Select
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          value={countryCode}
          onChange={(e) => onCountryChange(e.target.value)}
          data-testid={dataTestId}
          aria-label="country code"
        >
          {countryCodes.map((code) => {
            return (
              <option key={code} value={code}>
                {getCountryName(code)}
                &nbsp; +{getCountryCallingCode(code)}
              </option>
            );
          })}
        </Select>
      </SelectContainer>
      <StyledPhoneInput
        placeholder={t('PHONE_NUMBER')}
        country={countryCode}
        value={phoneNumber}
        // Make sure it's set to null rather than undefined
        onChange={(val) => (val ? setPhoneNumber(val) : setPhoneNumber(null))}
        hasError={hasError}
        errorColor={errorColor}
        aria-labelledby={ariaLabelledBy}
      />
    </PhoneInputs>
  );
};

PhoneNumberInput.propTypes = {
  phoneNumber: PropTypes.string,
  setPhoneNumber: PropTypes.func.isRequired,
  countryCode: PropTypes.string,
  hasError: PropTypes.bool,
  errorColor: PropTypes.string,
};

PhoneNumberInput.defaultProps = {
  countryCode: 'US',
  hasError: false,
  errorColor: '#ed1c24',
};
